<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5">
      <div>
        <!-- Page Title -->
        <PageTitle :title="'Purchase Order Details'" class="pb-5"></PageTitle>

        <!-- Purchase Order Summary -->
         <div class="d-flex justify-end mb-4 mr-4">
          <v-btn color="primary" @click="printTable">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
        </div>
        <v-row>
          <v-col cols="12">
            <v-card class="mb-5 pa-4">
              <v-card-title>
                <span class="headline">Purchase Order #{{ purchaseOrder.po_number }}</span>
              </v-card-title><br>
              <v-card-subtitle>
                <div>
                  <strong>Requisition Number:</strong> {{ purchaseOrder.requisition_number }}<br>
                  <strong>Supplier name:</strong> {{ purchaseOrder.supplier_name }}<br>
                  <strong>PO Date:</strong> {{ purchaseOrder.po_date }}<br>
                  <strong>Status:</strong>
                  <v-chip dark :color="statusColor(purchaseOrder.status)">
                    {{ stringFormatting(purchaseOrder.status) }}
                  </v-chip>
                </div>
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>

        <!-- Items Table -->
        <v-data-table
          v-if="items && items.length > 0"
          :headers="itemHeaders"
          :items="items"
          dense
          item-key="id"
          hide-default-footer
        >
          <template v-slot:item="{ item, index }">
            <tr
              :class="[index % 2 === 0 ? 'dark-row' : 'light-row', 'custom-row']"
              @click="goToItemDetailPage(item)"
            >
              <td>{{ item.product_code}}</td>
              <td>{{ item.description }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ item.unit_price }}</td>
              <td>{{ item.total_price }}</td>
            </tr>
          </template>
        </v-data-table>

        <!-- Grand Total Row -->
        <div v-if="items && items.length > 0" class="d-flex justify-end mt-4">
          <div class="pa-3" style="min-width: 300px; max-width: 350px;">
            <div class="d-flex justify-space-between align-center">
              <span style="font-weight: bold; font-size: 1.1em;">Grand Total:</span>
              <span style="font-size: 1.1em;">{{ grandTotal.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <v-alert v-else type="info" class="mt-5" outlined>
          No items found for this purchase order.
        </v-alert>
      </div>
    </v-card>
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle";
import { PurchaseOrderService } from "@/services/purchaseorder-service.js";

export default {
  components: { PageTitle },
  data() {
    return {
      purchaseOrder: {}, // Stores purchase order details
      items: [], // Stores items related to the purchase order
      loading: false,
      itemHeaders: [
        { text: "Item Name", value: "product_code" },
        { text: "Description", value: "description" },
        { text: "Quantity", value: "quantity" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Total Price", value: "total_price" },
      ],
    };
  },
  created() {
    this.fetchPurchaseOrderDetails();
    this.fetchPurchaseOrderItems();
   
    
    console.log("params: ",this.$route.params);
    
  },
  computed: {
    grandTotal() {
      return this.items.reduce((sum, item) => sum + Number(item.total_price || 0), 0);
    },
  },
  methods: {
    async fetchPurchaseOrderDetails() {
      try {
        const response = await PurchaseOrderService.getPurchaseOrderDetails(
          this.$route.params.uuid
        );
        this.purchaseOrder = response;
      } catch (error) {
        console.error("Error fetching purchase order details:", error);
      }
    },
    async fetchPurchaseOrderItems() {
      try {
        const response = await PurchaseOrderService.getItemsByPoNumber(
          this.$route.params.po_number
        );
        this.items = response; // Assign the fetched items
      } catch (error) {
        console.error("Error fetching items for purchase order:", error);
      }
    },

    printTable(){
      // Purchase Order Details
      const poDetails = `
        <div class="po-header">
          <h1>PURCHASE ORDER</h1>
          <div class="po-meta">
            <div><strong>PO Number:</strong> ${this.purchaseOrder.po_number || ""}</div>
            <div><strong>Requisition Number:</strong> ${this.purchaseOrder.requisition_number || ""}</div>
            <div><strong>Supplier Name:</strong> ${this.purchaseOrder.supplier_name || ""}</div>
            <div><strong>PO Date:</strong> ${this.purchaseOrder.po_date || ""}</div>
            <div><strong>Status:</strong> ${this.stringFormatting(this.purchaseOrder.status) || ""}</div>
          </div>
        </div>
        <hr>
      `;

      // Table HTML
      let tableHtml = `
        <table>
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total Price</th>
            </tr>
          </thead>
          <tbody>
            ${this.items.map(item => `
              <tr>
                <td>${item.product_code}</td>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>${item.unit_price}</td>
                <td>${item.total_price}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      // Calculate grand total
      const grandTotal = this.items.reduce(
        (sum, item) => sum + Number(item.total_price || 0),
        0
      );

      // Footer
      const poFooter = `
        <div class="po-footer">
          <hr>
          <div style="text-align:right; font-size:1.2em; margin-top:20px;">
            <strong>Grand Total:</strong> ${grandTotal.toFixed(2)}
          </div>
        </div>
      `;

      // Open print window
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Purchase Order</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; background: #f9f9f9; }
              .po-container { background: #fff; border: 1px solid #ddd; border-radius: 8px; padding: 32px; max-width: 800px; margin: auto; }
              .po-header { text-align: center; margin-bottom: 24px; }
              .po-header h1 { margin-bottom: 8px; font-size: 2.2em; letter-spacing: 2px; }
              .po-meta { text-align: left; margin-top: 16px; font-size: 1.1em; }
              hr { border: none; border-top: 1px solid #eee; margin: 24px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 16px; }
              th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
              th { background: #f5f5f5; }
              .po-footer { margin-top: 32px; }
            </style>
          </head>
          <body>
            <div class="po-container">
              ${poDetails}
              ${tableHtml}
              ${poFooter}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
,




    // goToItemDetailPage(item) {
    //   this.$router.push({ name: "item-detail", params: { itemId: item.id } });
    // },
    statusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "approved":
          return "green";
        case "cancelled":
          return "red";
        default:
          return "grey";
      }
    },
    stringFormatting(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
  },
};
</script>
<style scoped>
.first-row {
  font-weight: bold;
}
.dark-row {
  background-color: #f4f4f4;
}
.light-row {
  background-color: #ffffff;
}
.custom-row:hover {
  background-color: #e8e8e8;
}
</style>
